/**
 * TreeCanvas — Main tree rendering area with toolbar,
 * dot-grid background, and keyboard shortcut handling.
 */

import { useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useBuilderStore, useStrategy, useSelectedNodeId, useCanUndo, useCanRedo, useNodeCount } from '../../stores/builderStore';
import { TreeNode } from './TreeNode';

export function TreeCanvas() {
  const strategy = useStrategy();
  const selectedNodeId = useSelectedNodeId();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const nodeCount = useNodeCount();

  const undo = useBuilderStore((s) => s.undo);
  const redo = useBuilderStore((s) => s.redo);
  const deleteNode = useBuilderStore((s) => s.deleteNode);
  const copyNode = useBuilderStore((s) => s.copyNode);
  const cutNode = useBuilderStore((s) => s.cutNode);
  const pasteNode = useBuilderStore((s) => s.pasteNode);
  const duplicateNode = useBuilderStore((s) => s.duplicateNode);
  const selectNode = useBuilderStore((s) => s.selectNode);
  const expandAll = useBuilderStore((s) => s.expandAll);
  const collapseAll = useBuilderStore((s) => s.collapseAll);
  const moveNodeUp = useBuilderStore((s) => s.moveNodeUp);
  const moveNodeDown = useBuilderStore((s) => s.moveNodeDown);

  // ─── Keyboard Shortcuts ─────────────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if ((ctrl && e.key === 'z' && e.shiftKey) || (ctrl && e.key === 'y')) { e.preventDefault(); redo(); return; }

      if (!selectedNodeId) return;

      if (ctrl && e.key === 'c') { e.preventDefault(); copyNode(selectedNodeId); return; }
      if (ctrl && e.key === 'x') { e.preventDefault(); cutNode(selectedNodeId); return; }
      if (ctrl && e.key === 'v') { e.preventDefault(); pasteNode(selectedNodeId); return; }
      if (ctrl && e.key === 'd') { e.preventDefault(); duplicateNode(selectedNodeId); return; }
      if (e.key === 'Delete') { e.preventDefault(); deleteNode(selectedNodeId); return; }
      if (e.key === 'Escape') { e.preventDefault(); selectNode(null); return; }
      if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); moveNodeUp(selectedNodeId); return; }
      if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); moveNodeDown(selectedNodeId); return; }
    },
    [selectedNodeId, undo, redo, copyNode, cutNode, pasteNode, duplicateNode, deleteNode, selectNode, moveNodeUp, moveNodeDown],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Deselect on canvas click
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) selectNode(null);
    },
    [selectNode],
  );

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-surface-700 bg-surface-800/50 shrink-0">
        <ToolButton icon="undo" label="Undo" shortcut="Ctrl+Z" onClick={undo} disabled={!canUndo} />
        <ToolButton icon="redo" label="Redo" shortcut="Ctrl+Y" onClick={redo} disabled={!canRedo} />
        <div className="w-px h-5 bg-surface-700 mx-1" />
        <ToolButton icon="expand" label="Expand All" onClick={expandAll} />
        <ToolButton icon="collapse" label="Collapse All" onClick={collapseAll} />
        <div className="flex-1" />
        <span className="text-[10px] text-surface-500">{nodeCount} nodes</span>
      </div>

      {/* Tree */}
      <div
        className={clsx(
          'flex-1 overflow-y-auto overflow-x-hidden p-6',
          // Dot grid background
          'bg-surface-900',
        )}
        style={{
          backgroundImage: 'radial-gradient(circle, var(--color-surface-700) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
        onClick={handleCanvasClick}
      >
        <TreeNode node={strategy.root} depth={0} isRoot />
      </div>
    </div>
  );
}

// ─── Toolbar Button ──────────────────────────────────────────

function ToolButton({
  icon,
  label,
  shortcut,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const iconPath = {
    undo: 'M3 10h10a5 5 0 015 5v0a5 5 0 01-5 5H3',
    redo: 'M21 10H11a5 5 0 00-5 5v0a5 5 0 005 5h10',
    expand: 'M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5',
    collapse: 'M9 9L4 4m0 0h5M4 4v5m11-1l5-5m0 0h-5m5 0v5M9 15l-5 5m0 0h5m-5 0v-5m11 1l5 5m0 0h-5m5 0v-5',
  }[icon] || '';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={shortcut ? `${label} (${shortcut})` : label}
      className={clsx(
        'w-7 h-7 flex items-center justify-center rounded-md transition-colors',
        disabled
          ? 'text-surface-600 cursor-not-allowed'
          : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50',
      )}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={iconPath} />
      </svg>
    </button>
  );
}
